import { useLayoutEffect, useRef, type ReactNode } from 'react';

import { BOARD_ROW_PX, boardShape, packBoard } from '../../../lib/boardLayout';

/** Two alarm cards need this much room in the content area, beside any sidebar. */
const TWO_COLUMN_MIN_PX = 900;

/**
 * TOM-673: pack system cards at their own heights, using the board's tested
 * reading-order layout. The DOM stays in list order; a later card can fill
 * the shorter lane but never draw above an earlier card.
 */
export function AlarmGroupLayout({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const grid = ref.current;
    if (grid === null) return;
    const items = [...grid.children] as HTMLElement[];
    if (items.length === 0) {
      delete grid.dataset.columns;
      return;
    }
    const sizes = items.map(() => 'm' as const);

    const pack = () => {
      // The shared packer uses four logical columns for two half-width
      // cards, and two for a single full-width card on a narrow screen.
      const columns = grid.getBoundingClientRect().width >= TWO_COLUMN_MIN_PX ? 4 : 2;
      grid.dataset.columns = String(columns);
      grid.style.setProperty('--al-group-row', `${BOARD_ROW_PX}px`);
      const shape = boardShape(sizes, columns);
      items.forEach((item, i) => {
        // Clear an old right-hand placement before narrowing to one card:
        // otherwise that column would briefly create an implicit extra lane.
        item.style.setProperty('--al-group-col', '1');
        item.style.setProperty('--al-group-span', String(shape.spans[i]));
      });

      // Apply final widths before measuring: a lone filtered card fills the
      // grid, and an editor's wrapping can change when columns change.
      const heights = items.map((item) => item.getBoundingClientRect().height);
      const gap = Number.parseFloat(getComputedStyle(grid).columnGap) || 0;
      packBoard(sizes, columns, heights, gap).forEach((place, i) => {
        const item = items[i]!;
        item.style.setProperty('--al-group-col', String(place.col));
        item.style.setProperty('--al-group-start', String(place.row));
        item.style.setProperty('--al-group-rows', String(place.rows));
      });
    };

    pack();
    // Observe natural card heights, never the grid we resize. This handles
    // inline editors, save receipts and new readings without observer loops.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pack);
    for (const item of items) observer?.observe(item, { box: 'border-box' });
    window.addEventListener('resize', pack);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', pack);
    };
  }, [children]);

  return (
    <div className="al-groups" ref={ref}>
      {children}
    </div>
  );
}
